import { useState, useEffect, useContext, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Toolbar from "@mui/material/Toolbar";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { EventContext } from "../contexts/EventContext";
import AuthContext from "../contexts/AuthContext";
import request from "../utilities/requests";
import { logout } from "../utilities/authFlow";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import eventBus from "../utilities/EventBus";
import SettingsDialog from "./SettingsDialog";
import NotificationIcon from "./NotificationIcon";
import MessagingIcon from "./MessagingIcon";
import { useMediaQuery } from "@mui/material";

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 0;

const NavListItem = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const theme = useTheme();

  return (
    <ListItem
      component={NavLink}
      to={to}
      onClick={onClick}
      style={{
        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
        textDecoration: "none",
        borderTopRightRadius: 20,
        borderBottomRightRadius: 20,
        paddingLeft: "16px",
      }}
    >
      <Typography
        variant="button"
        style={{ color: isActive ? "white" : "black" }}
      >
        {children}
      </Typography>
    </ListItem>
  );
};

export default function ResponsiveDrawer({ children }) {
  const navigate = useNavigate();
  const { setSignedIn } = useContext(AuthContext);
  const { userData, setUserData } = useContext(EventContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  // State for desktop sidebar collapse/expand
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    if (!userData) {
      // getUser();
    }
    const requireTermsAck = localStorage.getItem('requireTermsAck') === 'true';
    const requireMessageAck = localStorage.getItem('requireMessageAck') === 'true';
    if (requireTermsAck || requireMessageAck) {
      eventBus.emit("SHOW_SYSTEM_MESSAGE", { requireTermsAck, requireMessageAck });
    }
    

  }, []);

  const getUser = async () => {
    try {
      // let userId = user.attributes["custom:userId"];

      const result = await request(`users`);
      if (!result?.success) {
        alert(result?.error);
        return;
      } else if (!result.user) {
        alert("User not found. Please contact support");
        return;
      }

      setUserData(result.user);
      if (result.user?.org?.style?.favicon) {
        let fav = document.getElementById("favicon");
        fav.setAttribute("href", result.user.org.style.favicon);
      }
      if (result.user?.org?.style?.title) {
        document.title = result.user.org.style.title;
      }
    } catch (e) {
      const error = `Error retrieving user information. Error: ${e.message}`;
      console.error(error);
      alert(error);
    }
  };

  const toggleDrawer = () => {
    setMobileOpen((prev) => !prev);
  };

  const closeDrawer = () => {
    setMobileOpen(false);
  };

  // Toggle function for desktop sidebar
  const toggleDesktopSidebar = () => {
    setDesktopOpen((prev) => !prev);
  };

  // const renderMatchmaking = () => {
  //   if (
  //     userData?.role === "superadmin" ||
  //     userData?.permissions?.canViewMatchmaking
  //   ) {
  //     return (
  //       <NavListItem to="/matchmaking" onClick={closeDrawer}>
  //         MATCHMAKING
  //       </NavListItem>
  //     );
  //   }
  //   return null;
  // };

    const renderOrders = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canManageOrders
    ) {
      return (
        <NavListItem to="/orders" onClick={closeDrawer}>
          ORDERS
        </NavListItem>
      );
    }
    return null;
  };

  const renderProfileQuestions = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canEditProfileQuestions
    ) {
      return (
        <NavListItem to="/profilequestions" onClick={closeDrawer}>
          PROFILE QUESTIONS
        </NavListItem>
      );
    }
    return null;
  };

  const renderMessenger = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canViewMessageTemplates
    ) {
      return (
        <NavListItem to="/messenger" onClick={closeDrawer}>
          MESSENGER
        </NavListItem>
      );
    }
    return null;
  };

  const renderAdmin = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canViewAdmin
    ) {
      return (
        <NavListItem to="/admin" onClick={closeDrawer}>
          ADMIN
        </NavListItem>
      );
    }
    return null;
  };

  const renderBlackList = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canEditBlackList
    ) {
      return (
        <NavListItem to="/blacklist" onClick={closeDrawer}>
          BLACKLIST
        </NavListItem>
      );
    }
    return null;
  };

  const renderAds = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canManageAds
    ) {
      return (
        <NavListItem to="/ads" onClick={closeDrawer}>
          ADS
        </NavListItem>
      );
    }
    return null;
  };

  const renderPrompts = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canEditPrompts
    ) {
      return (
        <NavListItem to="/prompts" onClick={closeDrawer}>
          PROMPTS
        </NavListItem>
      );
    }
    return null;
  };

  const renderReports = () => {
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canViewReports
    ) {
      return (
        <NavListItem to="/reports" onClick={closeDrawer}>
          REPORTS
        </NavListItem>
      );
    }
    return null;
  };

  const renderSystemMessages = () => {
    if (userData?.role === "superadmin") {
      return (
        <NavListItem to="/systemmessages" onClick={closeDrawer}>
          SYS MESSAGES
        </NavListItem>
      );
    }
    return null;
  };

    const renderScanner = () => {
    if (userData?.org?.config?.ticketingEnabled) {
      return (
        <NavListItem to="/scanner" onClick={closeDrawer}>
          SCANNER
        </NavListItem>
      );
    }
    return null;
  };

  const renderCalendar = () => {
    if (userData?.role === "superadmin" ||
      userData?.permissions?.canViewCalendar) {
      return (
        <NavListItem to="/calendar" onClick={closeDrawer}>
          CALENDAR
        </NavListItem>
      );
    }
    return null;
  };

    const renderWaivers = () => {
    if (userData?.role === "superadmin" ||
      (userData?.org?.config?.waiversActive && userData?.permissions?.canManageWaivers)) {
      return (
        <NavListItem to="/waivers" onClick={closeDrawer}>
          WAIVERS
        </NavListItem>
      );
    }
    return null;
  };

  const handleSignOut = async () => {
    console.log("Signing out...");
    try {
      await logout();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      window.location.reload();
    }
  };

  const drawer = (
    <div>
      <Toolbar style={{ display: "flex", justifyContent: "center" }}>
        <img
          src={userData?.org?.style?.logo ? userData.org.style.logo : ""}
          alt="logo"
          style={{ height: 100, width: 100 }}
        />
      </Toolbar>
      {/* Collapse/Expand Button for Desktop */}
      <Box
        sx={{
          position: "fixed",
          top: "50px",
          left: desktopOpen ? expandedDrawerWidth - 20 : 0, // Adjust offset so button appears bisected when expanded
          transform: "translateY(-50%)",
          zIndex: 1301,
          display: { xs: "none", sm: "block" },
        }}
      >
        <IconButton
          onClick={toggleDesktopSidebar}
          sx={{
            // border: "1px solid black",
            boxShadow:
              "2px 0 2px -2px rgba(0,0,0,0.2), 1px 0 3px 0 rgba(0,0,0,0.14), 1px 0 4px 0 rgba(0,0,0,0.12)",
            borderRadius: "50%",
            backgroundColor: "white",
            padding: "2px",
            marginRight: "-12px", // Negative margin shifts the button so it's half outside the drawer
          }}
        >
          {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <NavListItem to="/" onClick={closeDrawer}>
          HOME
        </NavListItem>
        <NavListItem to="/users" onClick={closeDrawer}>
          USERS
        </NavListItem>
        <NavListItem to="/events" onClick={closeDrawer}>
          EVENTS
        </NavListItem>
        {renderCalendar()}
        {renderOrders()}
        {renderProfileQuestions()}
        {renderMessenger()}
        {/* {renderMatchmaking()} */}
        {renderPrompts()}
        {renderBlackList()}
        {renderAds()}
        {renderReports()}
        {renderWaivers()}
        <NavListItem to="/help" onClick={closeDrawer}>
          SUPPORT
        </NavListItem>
        {renderSystemMessages()}
        {renderScanner()}
        {renderAdmin()}
        <Box sx={{ marginTop: "auto", display: "flex", justifyContent: "center", paddingY: 2 }}>
          <IconButton
            onClick={() => setSettingsOpen(true)}
            aria-label="settings"
            sx={{ marginRight: 2 }}
            
          >
            <SettingsIcon />
          </IconButton>
          <IconButton onClick={handleSignOut} aria-label="logout">
            <LogoutIcon />
          </IconButton>
        </Box>
      </List>
      <Divider />
    </div>
  );

  // Create a dynamic theme based on userData.org.style.primaryColor
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        primary: {
          main: userData?.org?.style?.primaryColor || "#1976d2", // Fallback to MUI's default blue
        },
      },
      typography: {
        fontFamily: ['"Instrument Sans"', "sans-serif"].join(","),
      },
    });
  }, [userData]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: "100%", display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            display: { xs: "flex", sm: "none" }, // Show on mobile (xs) and hide on larger screens (sm and above)
            width: "100%", // Full width on mobile
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {userData?.org?.name ? `${userData.org.name} Organizer` : "Organizer"}
            </Typography>
            {!isDesktop && (
              <Box sx={{ ml: 'auto', mr: 1, display: "flex", alignItems: "center" }}>
                {userData?.org?.config?.smsEnabled && (
                  <>
                    <MessagingIcon color="white" />
                    <Box sx={{ width: 4 }} />
                  </>
                )}
                <NotificationIcon color="white" />
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{
            width: {
              // xs: "100%",
              sm: desktopOpen ? expandedDrawerWidth : collapsedDrawerWidth,
            },
            flexShrink: { sm: 0 },
          }}
          aria-label="mailbox folders"
        >
          {/* Mobile Drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={closeDrawer}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: expandedDrawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>
          {/* Permanent Drawer for Desktop */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: desktopOpen ? expandedDrawerWidth : collapsedDrawerWidth,
                transition: "width 0.3s",
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box component="main" style={{ width: "100%" }}>
          <Toolbar />
          {children}
        </Box>
        {isDesktop && (
          <Box
            sx={{
              position: "fixed",
              top: 16,
              right: 24,
              display: "flex",
            }}
          >
            {userData?.org?.config?.smsEnabled && <MessagingIcon />}
            <NotificationIcon />
          </Box>
        )}
        <SettingsDialog open={settingsOpen} close={() => setSettingsOpen(false)}/>
      </Box>
    </ThemeProvider>
  );
}
